import React, { useState, useEffect, startTransition, useRef } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Heading,
  Text,
  Alert,
  AlertIcon,
  InputGroup,
  InputRightElement,
  Link,
  Checkbox,
  useColorModeValue,
  FormErrorMessage,
  HStack,
  Icon,
  useToast,
} from "@chakra-ui/react";
import {
  ViewIcon,
  ViewOffIcon,
  CheckCircleIcon,
  WarningIcon,
} from "@chakra-ui/icons";

const SignupForm = () => {
  // Use refs to preserve form values across rerenders
  const formValuesRef = useRef({
    email: "",
    username: "",
    password: "",
    confirmPassword: ""
  });

  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [localError, setLocalError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const { register, error: authError, clearAuthError, isLoading: authLoading } = useAuth();

  // Log component rerenders and form state
  // console.log("🔥 SIGNUP RERENDER:", {
  //   email,
  //   username, 
  //   password: password ? "***" : "",
  //   confirmPassword: confirmPassword ? "***" : "",
  //   isLoading,
  //   authLoading,
  //   registrationSuccess,
  //   refValues: {
  //     email: formValuesRef.current.email,
  //     username: formValuesRef.current.username,
  //     password: formValuesRef.current.password ? "***" : "",
  //     confirmPassword: formValuesRef.current.confirmPassword ? "***" : ""
  //   }
  // });

  // Restore form values from ref if they got cleared
  useEffect(() => {
    if (!email && formValuesRef.current.email) {
      console.log("🔥 RESTORING EMAIL:", formValuesRef.current.email);
      setEmail(formValuesRef.current.email);
    }
    if (!username && formValuesRef.current.username) {
      console.log("🔥 RESTORING USERNAME:", formValuesRef.current.username);
      setUsername(formValuesRef.current.username);
    }
    if (!password && formValuesRef.current.password) {
      console.log("🔥 RESTORING PASSWORD");
      setPassword(formValuesRef.current.password);
    }
    if (!confirmPassword && formValuesRef.current.confirmPassword) {
      console.log("🔥 RESTORING CONFIRM PASSWORD");
      setConfirmPassword(formValuesRef.current.confirmPassword);
    }
  }, [email, username, password, confirmPassword]);
  const toast = useToast();
  const hasShownErrorRef = React.useRef(false);

  const formBg = useColorModeValue("var(--color-card-bg)", "var(--color-card-bg)");
  const borderColor = useColorModeValue("var(--color-border)", "var(--color-border)");

  useEffect(() => {
    if (authError && !hasShownErrorRef.current) {
      // Stop local loading spinner when an auth error occurs
      setIsLoading(false);
      hasShownErrorRef.current = true;
      startTransition(() => {
        toast({
          id: `signup-error-${Date.now()}`, // Unique ID prevents duplicates
          title: "Registration Failed",
          description: authError,
          status: "error",
          duration: 5000,
          isClosable: true,
          onCloseComplete: () => {
            clearAuthError();
            hasShownErrorRef.current = false;
          },
        });
      });
    } else if (!authError && hasShownErrorRef.current) {
      // Reset the flag when error is cleared
      hasShownErrorRef.current = false;
    }
  }, [authError, toast, clearAuthError]);

  const isPasswordShort = password.length > 0 && password.length < 8;
  const passwordsMatch =
    confirmPassword.length > 0 && password === confirmPassword;

  // Enable submit only when all fields are filled, passwords are valid, and terms are accepted
  const isFormFilled =
    email.trim() !== "" &&
    username.trim() !== "" &&
    password.trim() !== "" &&
    confirmPassword.trim() !== "";
  const isPasswordValid = password.length >= 8 && password === confirmPassword;
  const canSubmit = isFormFilled && isPasswordValid && acceptedTerms && !isLoading && !authLoading;

  const validateForm = () => {
    if (password !== confirmPassword) {
      setLocalError("Passwords do not match");
      return false;
    }

    if (password.length < 8) {
      setLocalError("Password must be at least 8 characters long");
      return false;
    }
    setLocalError("");
    return true;
  };

  const handleSubmit = async () => {
    console.log("🔥 SIGNUP: Button clicked - starting submission");
    setLocalError("");

    if (!validateForm()) {
      return;
    }

    console.log("🔥 SIGNUP: Starting form submission");
    setIsLoading(true);

    try {
      console.log("🔥 SIGNUP: Calling register function");
      const result = await register(email, username, password);
      console.log("🔥 SIGNUP: Register result:", result);
      if (result.success) {
        setRegistrationSuccess(true);
        console.log("🔥 SIGNUP: Registration successful, navigation should be handled by AuthContext");
        // Navigation will be handled by AuthContext after registration completes
      } else {
        // Ensure spinner stops on non-throwing failure responses
        setIsLoading(false);
      }
    } catch (err: any) {
      console.error("🔥 SIGNUP: Registration error:", err);
      const errorMessage =
        err.message || "An unexpected error occurred. Please try again.";
      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      setIsLoading(false); // Only reset loading on error
    } finally {
      // Don't reset isLoading here on success - let AuthContext/navigation handle it
      console.log("🔥 SIGNUP: Form submission finally block reached");
    }
  };

  return (
    <Box
      w="full"
      maxW="md"
      mx="auto"
      mt={8}
      p={6}
      borderWidth="1px"
      borderRadius="lg"
      borderColor={borderColor}
      boxShadow="lg"
      bg={formBg}
    >
      <VStack spacing={5}>
        <Heading size="lg">Create Account</Heading>
        <Text fontSize="sm" color="gray.500">
          Join our community today
        </Text>

        {localError && (
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            {localError}
          </Alert>
        )}


        <FormControl id="email" isRequired>
          <FormLabel>Email</FormLabel>
          <Input
            type="email"
            value={email}
            onChange={(e) => {
              console.log("🔥 EMAIL CHANGE:", e.target.value);
              formValuesRef.current.email = e.target.value;
              setEmail(e.target.value);
            }}
            placeholder="Enter your email"
            size="md"
            focusBorderColor="blue.500"
          />
        </FormControl>

        <FormControl id="username" isRequired>
          <FormLabel>Username</FormLabel>
          <Input
            type="text"
            value={username}
            onChange={(e) => {
              console.log("🔥 USERNAME CHANGE:", e.target.value);
              formValuesRef.current.username = e.target.value;
              setUsername(e.target.value);
            }}
            placeholder="Choose a username"
            size="md"
            focusBorderColor="blue.500"
          />
        </FormControl>

        <FormControl
          id="password"
          isRequired
          isInvalid={isPasswordShort && password.length > 0}
        >
          <FormLabel>Password</FormLabel>
          <InputGroup>
            <Input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => {
                formValuesRef.current.password = e.target.value;
                setPassword(e.target.value);
              }}
              placeholder="Create a password"
              size="md"
              focusBorderColor="blue.500"
            />
            <InputRightElement width="4.5rem">
              <Button
                type="button"
                h="1.75rem"
                size="sm"
                onClick={() => setShowPassword(!showPassword)}
                variant="ghost"
              >
                {showPassword ? <ViewOffIcon /> : <ViewIcon />}
              </Button>
            </InputRightElement>
          </InputGroup>
          {password.length > 0 && (
            <HStack 
              spacing={1} 
              fontSize="xs"
              mt={1}
              color={password.length >= 8 ? "green.500" : "orange.500"}
            >
              <Icon
                as={password.length >= 8 ? CheckCircleIcon : WarningIcon}
                w={3}
                h={3}
              />
              <Text>Password must be at least 8 characters</Text>
            </HStack>
          )}
        </FormControl>

        <FormControl
          id="confirmPassword"
          isRequired
          isInvalid={
            confirmPassword.length > 0 && !passwordsMatch && password.length > 0
          }
        >
          <FormLabel>Confirm Password</FormLabel>
          <Input
            type={showPassword ? "text" : "password"}
            value={confirmPassword}
            onChange={(e) => {
              formValuesRef.current.confirmPassword = e.target.value;
              setConfirmPassword(e.target.value);
            }}
            placeholder="Confirm your password"
            size="md"
            focusBorderColor="blue.500"
          />
          {confirmPassword.length > 0 && password.length > 0 && (
            <HStack 
              spacing={1}
              fontSize="xs"
              mt={1}
              color={passwordsMatch ? "green.500" : "red.500"}
            >
              <Icon
                as={passwordsMatch ? CheckCircleIcon : WarningIcon}
                w={3}
                h={3}
              />
              <Text>
                {passwordsMatch
                  ? "Passwords match"
                  : "Passwords do not match"}
              </Text>
            </HStack>
          )}
        </FormControl>

        <FormControl isRequired>
          <Checkbox
            isChecked={acceptedTerms}
            onChange={(e) => setAcceptedTerms(e.target.checked)}
            colorScheme="blue"
          >
            I agree to the{' '}
            <Link as={RouterLink} to="/terms" color="blue.500" textDecoration="underline">
              Terms of Service
            </Link>
            {' '}and{' '}
            <Link as={RouterLink} to="/privacy" color="blue.500" textDecoration="underline">
              Privacy Policy
            </Link>
          </Checkbox>
        </FormControl>

        <Button
          type="button"
          onClick={handleSubmit}
          colorScheme="blue"
          size="md"
          width="full"
          isLoading={isLoading}
          loadingText="Creating Account..."
          mt={4}
          isDisabled={!canSubmit}
        >
          Sign Up
        </Button>

        <Text fontSize="sm">
          Already have an account?{" "}
          <Link as={RouterLink} to="/login" color="blue.500">
            Log in
          </Link>
        </Text>
      </VStack>
    </Box>
  );
};

export default SignupForm;
